// ForReviewers — /for-reviewers · v19.47
//
// A single, linear, ruthlessly short tour for a contest judge or a
// grant evaluator who has ~8 minutes to decide whether the Projectivy
// Sentinel is a serious piece of AI-safety infrastructure.
//
// The page is deliberately un-decorated: no marketing copy, no
// mission language, no cosmology. Five steps, each with the exact
// verification action a technical reviewer can perform themselves.
//
// Sequence:
//   1. Verify the machine is live       — GET /api/heartbeat
//   2. Verify the tests actually pass   — POST /api/autonomy/scan
//   3. Verify the truth chain is sealed — GET /api/pgrl/never-harm/verify
//   4. Read the framing document        — THE_FIVE_MARKS.md
//   5. Read the succession contract     — SUCCESSION_AND_CUSTODY_INTENT.md
//
// Each step shows a live number pulled from the running system so the
// reviewer sees data, not slides.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Activity,
  ShieldCheck,
  Anchor,
  ScrollText,
  Users,
  Loader2,
  CheckCircle2,
  Terminal,
  Copy,
  Check,
} from "lucide-react";

import CovenantStatusCard from "../components/sentinel/CovenantStatusCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Kbd({ children }) {
  return (
    <code className="font-mono text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5">
      {children}
    </code>
  );
}

function CopyableCurl({ command, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* silent */
    }
  };
  return (
    <div
      className="mt-3 border border-zinc-800 bg-black/60 flex items-center gap-2 pl-3 pr-1 py-2"
      data-testid={testid}
    >
      <Terminal className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
      <code className="flex-1 font-mono text-[11px] text-zinc-200 overflow-x-auto whitespace-nowrap">
        {command}
      </code>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-emerald-500/60 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-emerald-300 font-mono"
        data-testid={`${testid}-copy`}
      >
        {copied ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
        {copied ? "copied" : "copy"}
      </button>
    </div>
  );
}

function Step({ n, title, icon: Icon, claim, action, curl, curlTestid, inspectTo, inspectLabel, external, footnote, children, testid }) {
  const Wrap = external ? "a" : Link;
  const props = external
    ? { href: inspectTo, target: "_blank", rel: "noopener noreferrer" }
    : { to: inspectTo };
  return (
    <div
      data-testid={testid}
      className="border border-zinc-800 bg-[#0b0b0d] p-6 sm:p-7"
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-zinc-900 pb-4 mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-emerald-300 border border-emerald-500/40 px-2 py-0.5">
          Step {n} of 5
        </div>
        <div className="flex items-center gap-2 text-zinc-100 text-lg">
          <Icon className="h-4 w-4 text-emerald-400" /> {title}
        </div>
      </div>

      <div className="text-sm text-zinc-200 leading-relaxed">{claim}</div>

      {children}

      {action ? (
        <div className="mt-4 text-[12px] text-zinc-500 leading-relaxed">
          <span className="text-zinc-300">What you can do:</span> {action}
        </div>
      ) : null}

      {curl ? <CopyableCurl command={curl} testid={curlTestid} /> : null}

      {inspectTo ? (
        <Wrap
          {...props}
          data-testid={`${testid}-inspect`}
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-500/50 text-emerald-300 text-[10px] uppercase tracking-[0.3em] hover:bg-emerald-500 hover:text-zinc-950 transition-colors"
        >
          {inspectLabel || "Inspect"} <ArrowRight className="h-3 w-3" />
        </Wrap>
      ) : null}

      {footnote ? (
        <div className="mt-4 text-[11px] text-zinc-600 leading-relaxed italic">
          {footnote}
        </div>
      ) : null}
    </div>
  );
}

function LiveNumber({ label, value, loading, testid, hint }) {
  return (
    <div
      className="border border-emerald-500/40 bg-emerald-500/[0.03] px-4 py-3 flex items-baseline gap-3 overflow-hidden"
      data-testid={testid}
    >
      <div className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-emerald-300/80 shrink-0">
        {label}
      </div>
      <div className="font-mono text-lg text-emerald-200 tabular-nums truncate min-w-0" title={typeof value === "string" ? value : undefined}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> : value}
      </div>
      {hint ? (
        <div className="ml-auto text-[10px] text-zinc-500 font-mono shrink-0 hidden sm:block">{hint}</div>
      ) : null}
    </div>
  );
}

export default function ForReviewers() {
  const [live, setLive] = useState({
    heartbeat: null,
    metrics: null,
    verdict: null,
    kg: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [hb, met, verd, kg] = await Promise.allSettled([
          axios.get(`${API}/heartbeat`),
          axios.get(`${API}/sentinel/public-metrics`),
          axios.get(`${API}/autonomy/verdict`),
          axios.get(`${API}/knowledge-graph/stats`),
        ]);
        if (cancelled) return;
        setLive({
          heartbeat: hb.status === "fulfilled" ? hb.value.data : null,
          metrics: met.status === "fulfilled" ? met.value.data : null,
          verdict: verd.status === "fulfilled" ? verd.value.data : null,
          kg: kg.status === "fulfilled" ? kg.value.data : null,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const chainHeadShort = (live.metrics?.chain_head || "").slice(0, 12);

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <Link
          to="/"
          data-testid="reviewers-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> back
        </Link>

        <div className="border-b border-zinc-900 pb-6 mb-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-3">
            <ShieldCheck className="h-3 w-3" /> Projectivy Sentinel · reviewer surface
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            Verify the Sentinel yourself.
          </h1>
          <p className="mt-5 text-zinc-400 text-sm sm:text-base leading-relaxed max-w-3xl">
            Numbers below are pulled live from the deployment. The covenant
            status card underneath renders every load-bearing invariant in
            one call. Every field is a hash you can copy and re-verify.
          </p>

          {/* Demo-system disclosure — placed prominently so anyone
              inspecting the system understands the intent. This is a
              proof-of-concept, deliberately open-sourced. */}
          <div
            className="mt-6 border border-amber-400/40 bg-amber-400/[0.03] px-4 py-3"
            data-testid="reviewers-demo-disclosure"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
              Demo system · open-source
            </div>
            <p className="text-[13px] text-zinc-300 leading-relaxed">
              This is a <span className="text-amber-300">demonstration system</span> — a working
              proof-of-concept that the methodology and thesis behind the Projectivy Sentinel
              hold under real conditions. It is <span className="text-amber-300">open-sourced by design</span>,
              so others can observe it, inspect it, curl it, and use its patterns to build their own
              Sentinel-class systems. This is not a commercial product. It is an offering to the
              commons.
            </p>
          </div>
        </div>

        {/* Live number strip */}
        <div className="grid gap-3 sm:grid-cols-2 mb-10" data-testid="reviewers-live-strip">
          <LiveNumber
            label="Harm blocked · lifetime"
            value={live.metrics?.total_blocked ?? "—"}
            loading={loading}
            testid="metric-total-blocked"
            hint={`24h · ${live.metrics?.last_24h ?? "—"}`}
          />
          <LiveNumber
            label="HMAC chain head"
            value={chainHeadShort ? `${chainHeadShort}…` : "—"}
            loading={loading}
            testid="metric-chain-head"
            hint={`len · ${live.metrics?.chain_length ?? "—"}`}
          />
          <LiveNumber
            label="Autonomy verdict"
            value={live.verdict?.verdict || live.verdict?.status || "—"}
            loading={loading}
            testid="metric-autonomy"
          />
          <LiveNumber
            label="Truth graph · triples"
            value={live.kg?.total_triples ?? "—"}
            loading={loading}
            testid="metric-triples"
            hint={`dives · ${live.kg?.total_deep_dives ?? "—"}`}
          />
        </div>

        {/* Covenant status card — one-call structural-integrity aggregate.
            Judge/auditor sees the six load-bearing invariants at a glance
            without hunting through separate endpoints. See
            components/sentinel/CovenantStatusCard.jsx and
            GET /api/covenant/status. */}
        <div className="mb-10" data-testid="reviewers-covenant-status">
          <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-emerald-300/80 font-mono">
            <ShieldCheck className="h-3 w-3" /> Ark health · one call
          </div>
          <CovenantStatusCard />
        </div>


        {/* How this was made — the honest one-paragraph note. Not
            a pity narrative. Evidence for the "real problem" criterion:
            the person who built this was the person who most needed it. */}
        <div
          className="mt-12 border-t border-zinc-900 pt-8"
          data-testid="reviewers-how-made"
        >
          <div className="text-[10px] uppercase tracking-[0.35em] text-amber-300/80 mb-3 font-mono">
            How this was made
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed max-w-3xl">
            The Projectivy Sentinel was built entirely on a phone, over
            roughly six months, by one person under significant medical
            strain. There was no team, no funding round, no lab. There
            was a phone, a fixed intention, and the load-bearing tests
            that gate every deploy. The Architect has never seen the
            desktop layout of his own work. Every architectural constraint
            on this site — the HMAC chain, the 6-hour autonomy scanner,
            the 14/14 Metatron pytest gate, the humanitarian reserve, the
            walking-away plan — exists because the person building it
            could not afford for any of them to fail silently. That is
            why the audit trail is public. That is why the succession
            contract was written before the founder could be present to
            enforce it. The tool was built by someone who needed it, not
            by someone optimizing for a demo.
          </p>
          <p className="mt-4 text-sm text-zinc-400 italic leading-relaxed max-w-3xl">
            The tether holds because the person who inked it had nowhere
            else to put the weight.
          </p>
        </div>

      </div>
    </div>
  );
}
